/**
 * useBoardEvents — subscribes to the per-board WebSocket and invalidates
 * the matching TanStack Query caches on every event.
 *
 * Reconnect strategy: on close (not initiated by us), retry after 1s,
 * then exponentially up to 30s. The hook owns a single WebSocket per
 * (boardId, mounted-instance); double-mounting via React Strict Mode
 * is handled by the cleanup function closing the previous socket.
 *
 * The gateway sends `{"event":"ping"}` frames every 30s as a wire-keepalive;
 * the hook ignores them. Real INSERT/UPDATE/DELETE payloads from
 * `chaos.notify_board_event()` invalidate the corresponding query.
 */
import type { BoardEvent } from '@chaos/types/kanban';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { env } from '../../env';
import { kanbanKeys } from '../api/kanban';

interface PingFrame {
  event: 'ping';
}
interface HelloFrame {
  event: 'hello';
  boardId: string;
}

type ServerFrame = BoardEvent | PingFrame | HelloFrame;

/**
 * Resolve the WS URL relative to the API base. Mirrors the apiFetch
 * logic: in dev Vite proxies /api and /ws; in prod the gateway sits
 * behind Caddy at chaos.01foundry.com.
 */
function wsUrl(boardId: string): string {
  // VITE_API_BASE_URL is the same-origin /api root (e.g. "" or
  // "https://chaos.01foundry.com"). We swap http→ws / https→wss and
  // drop the trailing /api segment.
  const base = env.VITE_API_BASE_URL;
  let url: URL;
  try {
    url = new URL(base, window.location.origin);
  } catch {
    url = new URL(window.location.origin);
  }
  const proto = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${url.host}/ws/boards/${boardId}/events`;
}

export interface UseBoardEventsOptions {
  enabled?: boolean;
  /** Override the WS URL — used by unit tests with mock servers. */
  url?: string;
  /** Custom WebSocket constructor — injected in tests. */
  WebSocketCtor?: typeof WebSocket;
}

export function useBoardEvents(
  boardId: string | undefined,
  opts: UseBoardEventsOptions = {},
): void {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);
  const attemptRef = useRef(0);

  // Stable strings for the effect deps — we don't want the effect to
  // re-run when the consumer passes a fresh object literal each render.
  const enabled = opts.enabled !== false;
  const overrideUrl = opts.url;
  const Ctor = opts.WebSocketCtor;

  useEffect(() => {
    if (!boardId || !enabled) return;

    const connect = (): void => {
      const Constructor = Ctor ?? WebSocket;
      const url = overrideUrl ?? wsUrl(boardId);
      const ws = new Constructor(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
      };
      ws.onmessage = (msgEvent) => {
        const data =
          typeof msgEvent.data === 'string'
            ? msgEvent.data
            : new TextDecoder().decode(msgEvent.data as ArrayBuffer);
        let frame: ServerFrame;
        try {
          frame = JSON.parse(data) as ServerFrame;
        } catch {
          return;
        }
        if (frame.event === 'ping' || frame.event === 'hello') return;
        if (frame.table === 'cards') {
          qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(boardId) });
          // Also bust the per-card cache so the slide-over refetches.
          if (typeof frame.data === 'object' && frame.data && 'id' in frame.data) {
            const cardId = String(frame.data.id as string);
            qc.invalidateQueries({ queryKey: kanbanKeys.card(cardId) });
          }
        }
        if (frame.table === 'columns') {
          qc.invalidateQueries({ queryKey: kanbanKeys.boardColumns(boardId) });
        }
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (closedByUs.current) return;
        // Exponential backoff: 1s, 2s, 4s, … capped at 30s.
        const delay = Math.min(30_000, 1_000 * 2 ** attemptRef.current);
        attemptRef.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // No-op — onclose fires next and triggers reconnect.
      };
    };

    closedByUs.current = false;
    connect();

    return () => {
      closedByUs.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
      try {
        wsRef.current?.close();
      } catch {
        // Already closed.
      }
      wsRef.current = null;
    };
  }, [boardId, enabled, overrideUrl, Ctor, qc]);
}
